import { useState, useRef, useEffect, useMemo } from 'react';
import OrderSummaryComponentList from './OrderSummaryComponentList.jsx';
import UpsellComponent from './Upsell.jsx';
import '../assets/scss/components/summary.scss';
import * as API from '../api/Service.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import Alert from 'react-bootstrap/Alert';
import Carousel from 'react-bootstrap/Carousel';
import { cloneWith } from 'lodash';
import StickyCTAPanel from './common/StickyCTAPanel.jsx';
import PaymentInfoModals from './common/PaymentInfoModals.jsx';
import { useErrorBoundary } from "react-error-boundary";

export default function Summary(props)
{
    // Setup API call for data
    const [summaryViewModel, setSummaryViewModel] = useState(null);
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    let buildID = urlParams.get('buildid') !== null ? urlParams.get('buildid') : 0;
    const [buildIDGiven, setBuildIDGiven] = useState(false); // what is this for?
    const [showUpsellPanel, setShowUpsellPanel] = useState(false);
    const [upsellProducts, setUpsellProducts] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    //console.log("summaryViewModel", summaryViewModel);

    const { showBoundary } = useErrorBoundary();

    // --- Dopple pricing setup ---
    const [doppleText, setDoppleText] = useState("Spread the cost on orders over £1000");

    // locally cache url until summaryViewModel.build.grossPrice changes
    const url = useMemo(() => {
        return summaryViewModel?.build.grossPrice
            ? `${process.env.REACT_APP_WEB_APP_API_URL}/Dopple/GetLowestFinancePlan?grossPrice=${summaryViewModel.build.grossPrice.toFixed(2)}`
            : "";
    }, [summaryViewModel?.build.grossPrice]);
    
    const FetchDopplePricingOptions = {
        method: 'GET',
        mode: 'cors', // no-cors, *cors, same-origin
        credentials: 'include', // include, *same-origin, omit
        headers: {
            'embed-site': process.env.REACT_APP_EMBED_SITE
        },
    }

    const fetchDopplePricingRefetch = API.useRefetchQuery("FetchDopplePricing", url, 3, FetchDopplePricingOptions);

    useEffect(() => 
    {
        if (!summaryViewModel?.dopplePay) return;
        if (!summaryViewModel?.build.grossPrice || !fetchDopplePricingRefetch) return;

        if (summaryViewModel?.build.grossPrice < 1000)
        {
            setDoppleText(`Spread the cost on orders over £1000`);
            return;
        } 

        fetchDopplePricingRefetch()
            .then((response) => 
            {
                if (response?.data?.errors && Object.keys(response.data.errors).length > 0) 
                {
                    throw new Error(JSON.stringify(response));
                }

                const data = response.data;
                if(data.monthlyPayment === 0)
                    setDoppleText(`Spread the cost on orders over £1000`);
                else 
                    setDoppleText(`From £${data.monthlyPayment.toFixed(2)} per month`);
            })
            .catch((error) => 
            {
                showBoundary(error);
            });
        }, [summaryViewModel?.dopplePay, summaryViewModel?.build.grossPrice, fetchDopplePricingRefetch, showBoundary]); // re-run when price changes

    // --- Dopple pricing setup end ---


    //#region Fetch Summary Data
    const fetchSummaryDataRefetch = API.useRefetchQuery("SummaryData", "/summary?buildid=" + buildID + "&istest=" + props.isTest, 1);

    // only fetch data if buildid provided
    if (!isFetching && buildID && buildID > 0 && upsellProducts === null && summaryViewModel === null)
    {
        setIsFetching(true);

        fetchSummaryDataRefetch()
            .then((response) =>
            {
                setIsFetching(false);

                if (response?.data?.errors && Object.keys(response.data.errors).length > 0)
                {
                    throw new Error(JSON.stringify(response));
                }

                return response.data;
            })
            .then((refetchData) =>
            {

                //console.log("setting summary model", refetchData);
                setSummaryViewModel(refetchData);
                setBuildIDGiven(true);
                return true; // call finished
            })
            .catch((error) =>
            {
                //setStatusMessage("Unable to retrieve order summary for your build - " + buildID + ". Please go back and try again. If the issue persists please contact customer services for further assistance.");
                showBoundary(error);
                setBuildIDGiven(false);
                setIsFetching(false);
            });
    }
    //#endregion Fetch Summary Data

    // Edit Build
    const handleEditBuild = () =>
    {
        // ensure build fetching build message is shown every time, not just the first time
        setSummaryViewModel(null);

        props.handleToggleSummary(false);
    }

    const handleEditBuildFromIncomplete = (item) =>
    {
        if (item.rightPossibleCategory != null)
        {
            // ensure build fetching build message is shown every time, not just the first time
            setSummaryViewModel(null);

            props.categoryItem.current = item;
            props.handleToggleSummary(false);
        }
    }

    //#region Add To Basket

    const buyBuildRefetch = API.useRefetchQuery("BuyBuild", "/build/buy?buildid=" + buildID, 1);

    const handleAddToBasket = (e) =>
    {
        e.stopPropagation();

        setIsLoading(true);

        buyBuildRefetch()
            .then((response) =>
            {

                if (response?.data?.errors && Object.keys(response.data.errors).length > 0)
                {
                    throw new Error(JSON.stringify(response));
                }


                //console.log("Got product: ");
                //console.log(response.data.productID);
                //console.log("About to add to basket");

                let noUpsellProducts = false;
                if (response.data.upsellProducts == null || response.data.upsellProducts.products == null || response.data.upsellProducts.products.length === 0)
                {
                    noUpsellProducts = true;
                }

                // this will prevent redirect to basket if upsell products are returned
                window.CclAddToBasket(response.data.productID, props.removeFromBasket, noUpsellProducts, process.env.NODE_ENV, false, response.data.productData, response.data.buildID, props.removeFromBasketBuildID);

                console.log(JSON.stringify(response.data));
                console.log("Added to basket");

                // fake re-direct to upsell - just show upsell component
                const queryString = window.location.search;
                let urlParams = new URLSearchParams(queryString);

                urlParams.delete("showsummary");
                window.history.replaceState(null, null, window.location.pathname + "?" + urlParams.toString());

                // as above, if there are no upsell products window.CclAddToBasket will redirect to basket else the below will show the upsell component
                if (response.data.upsellProducts.products.length > 0)
                {
                    setShowUpsellPanel(true);
                    setUpsellProducts(response.data.upsellProducts.products);
                }

                //setIsLoading(false); // don't reset this flag - jQuery add to basket call takes a few seconds
                return;
            })
            .catch((error) =>
            {
                //console.log("post buyBuildRefetch error: ", error);
                showBoundary(error);
            });

    }

    const handleGoToBasket = () =>
    {
        window.location.href = "/basket";
    }

    //#endregion Add To Basket

    //#region Share
    const handleShare = () =>
    {

        if (navigator.share)
        {
            navigator.share({
                title: document.title,
                url: document.location.href
            }).then(() =>
            {
                //console.log('Link shared');
            })
                .catch(console.error);
        }
        else
        {
            // fallback
            alert('Browser not supported');
        }
    }
    //#endregion Share

    // scroll to red info on short viewports
    const refIncompatibilityInfoAlert = useRef(null);

    const handleGoToDetails = () =>
    {
        if (refIncompatibilityInfoAlert != null)
        {
            refIncompatibilityInfoAlert.current.scrollIntoView({ behavior: 'smooth' });
        }
    }

    // show sticky cta panel if ATB or compat info panel not visible (short viewports)
    const refMainATBBtn = useRef(null);

    // temp
    const handleShowProducts = () =>
    {
        setShowUpsellPanel(true);
    }

    const closeModalButton = useRef(null);

    props.imageLoaded.current = false;

    //images

    // override prev/next image icon position
    const imageIndicatorsRef = useRef(null);

    useEffect(() =>
    {

        const targetNode = imageIndicatorsRef.current;

        if (targetNode)
        {
            // Create a Mutation Observer instance
            const observer = new MutationObserver(mutationsList =>
            {
                for (const mutation of mutationsList)
                {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0)
                    {
                        mutation.addedNodes.forEach(node =>
                        {
                            try
                            {
                                let carouselIndicators = node.querySelector('.carousel-indicators');

                                if (carouselIndicators && carouselIndicators.children.length > 0) 
                                {
                                    // these figures are a bit of a fudge because how the arrows sit by default aren't equal
                                    let leftArrow = ((carouselIndicators.clientWidth / 2) - 15) * -1;
                                    let rightArrow = (carouselIndicators.clientWidth / 2) - 35;

                                    let carouselElement = node.querySelector('.carousel');

                                    carouselElement.children[2].style.cssText = "left: calc(50% + " + leftArrow + "px);";
                                    carouselElement.children[3].style.cssText = "left: calc(50% + " + rightArrow + "px);";
                                }
                            }
                            catch
                            {
                                // do nothing - sometimes node = "0" for an unknown readon which breaks things
                            }
                        });
                    }
                }
            });

            // Start observing the target node for child list mutations
            observer.observe(targetNode, { childList: true, subtree: true });

            // Cleanup: disconnect the observer when the component is unmounted
            return () =>
            {
                observer.disconnect();
            };
        }

    }, []);

    // handle back
    const handleBack = () =>
    {
        window.history.replaceState(null, null, window.location.origin + window.location.pathname);
        window.location.reload();
    }

    useEffect(() =>
    {

        // Add event listener for popstate when the component mounts
        window.addEventListener('popstate', handleEditBuild);

        // Clean up the event listener when the component unmounts
        return () =>
        {
            window.removeEventListener('popstate', handleEditBuild);
        };

    });

    return (
        <div ref={imageIndicatorsRef}>
            {
                (buildIDGiven && summaryViewModel && !showUpsellPanel)
                    ?
                    (
                        <div className="container mx-0 mw-100 vh-100 custom-bg-dark">
                            <div className="summary-wrapper row mx-sm-auto custom-bg-dark">
                                <div className="summary-left-panel col-lg-7 mb-5 mb-lg-0 px-1 px-md-2 px-lg-5">
                                    <div className="summary-left-inner-panel mx-auto d-flex justify-content-center">
                                        <span className="tag-container px-3">
                                            <label className="tag-label mb-0 text-casing custom-color-dark">{summaryViewModel.warrantyText}</label>
                                        </span>

                                        {/* image carousel */}
                                        <div className="main-prebuilt-image-container d-flex justify-content-center pb-xl-0">

                                            <Carousel interval={null} indicators={summaryViewModel.images.length > 1 ? true : false} indicatorLabels={[""]}
                                                prevIcon={<span aria-hidden="true"><FontAwesomeIcon icon={icon({ name: 'chevron-left', style: 'solid' })} className={"prev-image-icon " + (summaryViewModel.images.length < 2 ? "d-none" : "")} /></span>}
                                                nextIcon={<span aria-hidden="true"><FontAwesomeIcon icon={icon({ name: 'chevron-right', style: 'solid' })} className={"next-image-icon " + (summaryViewModel.images.length < 2 ? "d-none" : "")} /></span>}
                                            >
                                                {
                                                    summaryViewModel.images.map((imageUrl, index) =>
                                                    {
                                                        return (
                                                            <Carousel.Item key={"image_" + index}>
                                                                <img src={summaryViewModel.settings.imageBaseUrl + imageUrl + "?width=1600&height=1600&scale=both"} className="pc-image d-block w-100 h-100 summary-left-image" alt={summaryViewModel.build.name + " image " + index} />
                                                            </Carousel.Item>
                                                        )
                                                    })
                                                }
                                            </Carousel>

                                        </div>

                                    </div>
                                </div>

                                <div className="summary-right-panel col-lg-5 px-1 px-sm-2">

                                    {/* Render Header */}
                                    <div className="summary-header mb-3">

                                        <p className="summary-build-id text-start text-casing text-silver-4 fw-normal mb-1">Build ID: {summaryViewModel.build.id}</p>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h1 className="summary-prebuilt-name mb-0 me-3 custom-color-light">{summaryViewModel.build.name}</h1>
                                            <span className={"summary-edit-build-link primary-underline-link text-casing text-nowrap " + (isLoading ? " invisible" : "")} onClick={handleEditBuild} >Edit Build</span>
                                        </div>
                                        {
                                            summaryViewModel.build.grossPrice
                                            &&
                                            <>
                                                <h3 className="mb-2 summary-build-price custom-color-light">&pound;{summaryViewModel.build.grossPrice.toFixed(2)} inc. VAT</h3>

                                                <StickyCTAPanel ctaType={2}
                                                    model={summaryViewModel}
                                                    handlePrimaryAction={handleAddToBasket}
                                                    handleSecondaryAction={handleGoToDetails}
                                                    primaryElementRef={refMainATBBtn}
                                                    secondaryElementRef={refIncompatibilityInfoAlert}
                                                    scrollingElementRef={null}
                                                    isLoading={isLoading}
                                                    doppleText={doppleText}
                                                    dopplePay={summaryViewModel?.dopplePay} />
                                            </>
                                        }
                                        {
                                            summaryViewModel && summaryViewModel.financeOptions && summaryViewModel.monthlyPayment
                                            &&
                                            <>
                                                <label className="text-muted">Order now from &pound;{summaryViewModel.monthlyPayment.toFixed(2)} per month with Novuna Finance.</label> <button type="button" className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline px-0" data-bs-toggle="modal" data-bs-target="#financeModal">Find out more</button>
                                            </>
                                        }
                                        {
                                            summaryViewModel && summaryViewModel.payPalCredit && summaryViewModel.payPalCredit.lowestMonthlyOption && summaryViewModel.payPalCredit.lowestMonthlyOption.monthlyPayment
                                            &&
                                            <>
                                                <label className="text-muted">Order now from &pound;{summaryViewModel.payPalCredit.lowestMonthlyOption.monthlyPayment.toFixed(2)} per month with PayPal Credit.</label> <button type="button" className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline px-0" data-bs-toggle="modal" data-bs-target="#payPalCreditModal">Find out more</button>
                                            </>
                                        }
                                        {
                                            summaryViewModel?.dopplePay
                                            &&
                                            <>                                            
                                                <span className="finance-info-container text-start">
                                                    <button type="button" className="paypal-modal-button bg-transparent border-0 text-light ps-0 cursor-pointer" data-bs-toggle="modal" data-bs-target="#doppleModal"><label className="text-light d-inline cursor-pointer">{doppleText}</label> <span className="position-relative finance-message-icon"><FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} /></span></button>
                                                </span>

                                                <PaymentInfoModals dopplePay={summaryViewModel.dopplePay} />
                                            </>

                                        }
                                        {
                                            (summaryViewModel && summaryViewModel.isExpress) ? <span className="d-flex pt-3 align-items-center"><FontAwesomeIcon className="pe-2" icon={icon({ name: 'truck', style: 'solid' })} />Delivered next working day for free when ordered before 1pm</span> : null
                                        }
                                    </div>

                                    {/* Render category/component lines */}
                                    {
                                        (summaryViewModel && summaryViewModel.buildItemSummary)
                                        &&
                                        <div className="mb-4">
                                            <OrderSummaryComponentList buildItemSummary={summaryViewModel.buildItemSummary} viewMode={1} />
                                        </div>
                                    }

                                    {

                                        props.viewMode
                                            ?
                                            <button type="button" className="primary-button px-5 w-100 mb-3 text-casing d-none d-lg-block" onClick={handleGoToBasket}>Return To Basket</button>
                                            :
                                            /******/
                                            (summaryViewModel && summaryViewModel.compatibilityInfo && summaryViewModel.compatibilityInfo.length > 0)
                                                ?
                                                <>
                                                    <Alert ref={refIncompatibilityInfoAlert} variant="danger" className="mb-4">
                                                        <Alert.Heading className="fw-bold">Incompatibility issue or incomplete build</Alert.Heading>
                                                        <p className="">
                                                            There are incompatible or missing items in your build.
                                                        </p>
                                                        <div className="mb-3">
                                                            <ul>
                                                                {summaryViewModel.compatibilityInfo.map((item, index) =>
                                                                {
                                                                    if (item.rightText != null)
                                                                    {
                                                                        item.rightText = item.rightText.replace("{", " <span class='text-danger'>")
                                                                        item.rightText = item.rightText.replace("}", " </span>")
                                                                    }

                                                                    return <li key={item.ruleid + "_" + index}><div onClick={() => { handleEditBuildFromIncomplete(item) }} className={item.rightPossibleCategory != null ? "mb-1 fw-bold red-underline-link" : "mb-1 fw-bold"}><p className="d-inline" key={"incompatibilityItem_" + index} dangerouslySetInnerHTML={{ __html: item.rightText }}></p></div></li>
                                                                }
                                                                )}
                                                            </ul>
                                                        </div>
                                                        <span className="summary-edit-build-link red-underline-link text-casing text-nowrap ps-1" onClick={handleEditBuild} >Edit Build</span>
                                                    </Alert>
                                                </>
                                                :
                                                <button ref={refMainATBBtn} className="primary-button px-5 w-100 mb-3 text-casing d-none d-lg-block custom-color-dark" onClick={handleAddToBasket} disabled={isLoading === true ? "disabled" : ""}>
                                                    {
                                                        isLoading
                                                            ?
                                                            <div className="d-flex justify-content-center align-items-center" role="status" style={{ "height": "1.2rem" }}>
                                                                <div className="spinner-border" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            </div>
                                                            :
                                                            <>Add To Basket</>
                                                    }
                                                </button>
                                        /******/
                                    }

                                    {/*TEMP*/}
                                    {/*<button className="white-button px-5 w-100 mb-3" onClick={handleShowProducts}>SHOW PRODUCTS</button>*/}

                                    <div className="d-flex">
                                        {/*Added ps-2 to cater for strange extra width on icon making it all look slightly off centre*/}
                                        <span className="primary-underline-link text-casing mx-auto ps-2" onClick={handleShare} >Share this build<span className="d-inline-block ms-1"><FontAwesomeIcon icon={icon({ name: 'share-nodes', style: 'solid' })} className="me-2" /></span></span>
                                    </div>

                                </div>
                            </div>
                        </div>
                    )
                    : /* !(buildIDGiven && summaryViewModel && !showUpsellPanel) */
                    (
                        showUpsellPanel
                            ?
                            <UpsellComponent buildID={buildID} products={upsellProducts} />
                            :
                            <div className="status-message-container m-5 pt-5 px-sm-5 text-center vh-100">
                                {
                                    !buildID || buildID === 0 ?
                                        (
                                            <>
                                                <h1 className="text-center lh-sm">Unable to retrieve order summary for your build. Please go back and try again. If the issue persists please contact customer services for further assistance.</h1>
                                                <span className="summary-edit-build-link primary-underline-link fs-5" onClick={handleBack}>BACK</span>
                                            </>
                                        )
                                        :
                                        (
                                            <h1 className="fading-ellipsis text-center lh-sm">
                                                Retrieving order summary - please wait<span>.</span><span>.</span><span>.</span>
                                            </h1>
                                        )
                                }
                            </div>
                    )
            }

            <PaymentInfoModals novunaOptions={summaryViewModel?.financeOptions} novunaTerms={summaryViewModel?.financeModalTerms} paypalOptions={summaryViewModel?.payPalCredit?.creditOptions} />
        </div>
    );
}
